import {MultiChoiceBLASTParameter, PrimitiveBLASTParameter} from 'search/blast/parameters/common';
import {
  BLASTCompAdjustment,
  BLASTCompAdjustmentLabels,
  BLASTGapCost,
  BLASTMatchScore,
  BLASTMatrix,
  BLASTNTask,
  BLASTOperator,
  BLASTPTask,
  BLASTTaskTypeMap,
  BLASTXTask,
  TBLASTNTask,
} from 'search/SearchConstants';
import {capitalize} from 'util/NameUtil';

export enum UIFieldKeys {
  TASK = 'task',
  EVALUE = 'evalue',
  WORD_SIZE = 'word_size',
  STRAND = 'strand',
  GAP_COST = 'gap_cost',
  MATCH_SCORE = 'match_score',
  CULLING_LIMIT = 'culling_limit',
  MATRIX = 'matrix',
  COMP_BASED_STATS = 'comp_based_stats',
}

export interface PrimitiveUIField<ValueType> {
  label: string;
  description?: string;
  value: ValueType;
}

export interface MultiChoiceUIField<ValueType> {
  label: string;
  description?: string;
  options: Array<{value: ValueType; display?: string}>;
  defaultValue: ValueType;
}

export const getTaskUIField = <T extends BLASTOperator>(task: BLASTTaskTypeMap[T]): string => {
  // BLASTN
  if (task === BLASTNTask.MegaBLAST) {
    return 'megablast (highly similar sequences)';
  } else if (task === BLASTNTask.DCMegaBLAST) {
    return 'discontiguous megablast (more dissimilar sequences)';
  } else if (task === BLASTNTask.BLASTN) {
    return 'blastn (somewhat similar sequences)';
  } else if (task === BLASTNTask.BLASTNShort) {
    return 'blastn-short (optimized for < 30 nucleotides)';
  }

  // BLASTP
  if (task === BLASTPTask.BLASTP) {
    return 'blastp (standard protein-protein comparisons)';
  } else if (task === BLASTPTask.BLASTPFast) {
    return 'blastp-fast (accelerated version with a larger word size)';
  } else if (task === BLASTPTask.BLASTPShort) {
    return 'blastp-short (optimized for < 30 residues)';
  }

  // BLASTX
  if (task === BLASTXTask.BLASTX) {
    return 'blastx (standard translated nucleotide-protein comparison)';
  } else if (task === BLASTXTask.BLASTXFast) {
    return 'blastx-fast (accelerated version with a larger word size)';
  }

  // TBLASTN
  if (task === TBLASTNTask.TBLASTN) {
    return 'tblastn (standard protein-translated nucleotide comparison)';
  } else if (task === TBLASTNTask.TBLASTNFast) {
    return 'tblastn-fast (accelerated version with a larger word size)';
  } else {
    throw new Error(`Unsupported BLAST task: ${task}`);
  }
};

export const getEvalueUIField = (evalue: PrimitiveBLASTParameter<number>): PrimitiveUIField<number> => ({
  label: 'Expect value (E-value)',
  description: 'The number of expected hits of similar quality (score) that could be found just by chance.',
  value: evalue.defaultValue,
});

export const getCullingLimitUIField = (
  cullingLimit: PrimitiveBLASTParameter<number>
): PrimitiveUIField<number> => ({
  label: 'Max matches in a query range',
  description:
    'Remove any hits that are completely enveloped by at least that number of ' +
    'higher-scoring hits. This option is useful if many strong matches to one ' +
    'part of a query may prevent BLAST from presenting weaker matches to another ' +
    'part of the query.',
  value: cullingLimit.defaultValue,
});

export const getWordSizeUIField = (
  wordSize: MultiChoiceBLASTParameter<number>
): MultiChoiceUIField<number> => ({
  label: 'Word size',
  description: 'The length of the seed that initiates an alignment.',
  options: wordSize.values.map((value: number) => ({
    value,
  })),
  defaultValue: wordSize.defaultValue,
});

export const getGapCostsUIField = (
  gapCosts: MultiChoiceBLASTParameter<BLASTGapCost>
): MultiChoiceUIField<BLASTGapCost> => ({
  label: 'Gap costs',
  description: 'Cost to create and extend a gap in an alignment.',
  options: gapCosts.values.map(({existence, extension}) => ({
    value: {existence, extension},
    display:
      extension === undefined
        ? 'Linear (determined by match score)'
        : `Existence: ${existence}, extension: ${extension}`,
  })),
  defaultValue: gapCosts.defaultValue,
});

export const getMatchScoreUIField = (
  matchScore: MultiChoiceBLASTParameter<BLASTMatchScore>
): MultiChoiceUIField<BLASTMatchScore> => ({
  label: 'Match / mismatch scores',
  description: 'Reward and penalty for matching and mismatching bases.',
  options: matchScore.values.map(({reward, penalty}) => ({
    value: {reward, penalty},
    display: `Match reward: ${reward}, mismatch penalty: ${penalty}`,
  })),
  defaultValue: matchScore.defaultValue,
});

export const getStrandUIField = (strand: MultiChoiceBLASTParameter<string>): MultiChoiceUIField<string> => ({
  label: 'Strand',
  description: 'Which strand(s) of the query sequence(s) to search against the database.',
  options: strand.values.map((value) => ({
    value,
    display: capitalize(value),
  })),
  defaultValue: strand.defaultValue,
});

export const getMatrixUIField = (
  matrix: MultiChoiceBLASTParameter<BLASTMatrix>
): MultiChoiceUIField<BLASTMatrix> => ({
  label: 'Scoring matrix',
  options: matrix.values.map((value) => ({
    value,
  })),
  defaultValue: matrix.defaultValue,
});

export const getCompBasedStatsUIField = (
  compBasedStats: MultiChoiceBLASTParameter<BLASTCompAdjustment>
): MultiChoiceUIField<BLASTCompAdjustment> => ({
  label: 'Composition-based statistics',
  options: compBasedStats.values.map((value) => ({
    value,
    display: BLASTCompAdjustmentLabels[value],
  })),
  defaultValue: compBasedStats.defaultValue,
});

export const getUIField = {
  [UIFieldKeys.TASK]: getTaskUIField,
  [UIFieldKeys.EVALUE]: getEvalueUIField,
  [UIFieldKeys.WORD_SIZE]: getWordSizeUIField,
  [UIFieldKeys.STRAND]: getStrandUIField,
  [UIFieldKeys.GAP_COST]: getGapCostsUIField,
  [UIFieldKeys.MATCH_SCORE]: getMatchScoreUIField,
  [UIFieldKeys.CULLING_LIMIT]: getCullingLimitUIField,
  [UIFieldKeys.MATRIX]: getMatrixUIField,
  [UIFieldKeys.COMP_BASED_STATS]: getCompBasedStatsUIField,
};
